<template>
  <div class="select-checklist" :readonly="readonly">
    <InputComponent
      v-if="hasSearch && !disabled"
      v-model="filter.search"
      @keydown="performQuery"
      :icon="['fal', 'search']"
      :placeholder="$t('search') + '...'"
      class="select-checklist__search"
    />
    <LoaderComponent :value="promises.fetch" theme="default">
      <div class="select-checklist__items">
        <slot />
        <template v-if="selectAll && itemsComp.length > 1">
          <CheckboxComponent :value="isSelected()" :disabled="disabled" @input="selectMany" :label="$t('selectAll')" />
          <hr />
        </template>
        <template v-if="groupBy">
          <div v-for="(group, groupIndex) in groupComp" :key="group.value">
            <hr v-if="groupIndex > 0" class="mt-2 mb-2" />
            <div v-if="group.label" class="mb-1">
              <b>{{ group.label }}</b>
            </div>
            <template v-if="group.items.length > 1 && groupBy.selectAll">
              <CheckboxComponent
                @input="$ev => selectMany($ev, groupBy.path, group.value)"
                :value="isSelected(groupBy.path, group.value)"
                :disabled="disabled"
                :label="$t('selectAll')"
              />
              <hr />
            </template>
            <div class="col g-1">
              <CheckboxComponent
                v-for="(item, index) in group.items"
                v-bind="$attrs"
                :value="value"
                @input="$ev => $emit('input', $ev)"
                :key="`${group.value}_${index}`"
                :state="item.value"
                :label="item.label"
                :sublabel="item.sublabel"
                :disabled="disabled || item.disabled"
              >
                <template #default>
                  <span v-html="item.label"></span>
                </template>
              </CheckboxComponent>
            </div>
          </div>
        </template>
        <template v-else>
          <CheckboxComponent
            v-for="(item, index) in itemsComp"
            v-bind="$attrs"
            :value="value"
            @input="$ev => $emit('input', $ev)"
            :key="index"
            :state="item.value"
            :label="item.label"
            :sublabel="item.sublabel"
            :disabled="disabled || item.disabled"
          >
            <template #default>
              <span v-html="item.label"></span>
              <img v-if="item.icon" class="ml-1" v-bind="item.icon" />
            </template>
          </CheckboxComponent>
        </template>
        <slot v-if="!value.length" name="noresults" :filter="filter" />
      </div>
    </LoaderComponent>
  </div>
</template>

<script>
import { InputComponent } from 'vue-elder-input'
import { LoaderComponent } from 'vue-elder-loader'
import { CheckboxComponent } from 'vue-elder-checkbox'
import { Debounce } from '@/utils'

export default {
  props: {
    search: Boolean,
    readonly: Boolean,
    disabled: Boolean,
    selectAll: Boolean,
    groupBy: Object,
    value: Array,
    items: {
      type: [Function, Array],
      required: true,
    },
  },
  watch: {
    items: {
      handler(val) {
        if (typeof val === 'function') this.internalfetch(this.filter)
      },
      immediate: true,
    },
  },
  data() {
    return {
      fetchedItems: [],
      selected: [],
      promises: {
        fetch: null,
      },
      filter: {
        search: null,
      },
    }
  },
  computed: {
    groupComp() {
      if (!this.groupBy) return
      return Object.values(
        this.itemsComp.reduce((res, item) => {
          let value = this.$path(this.groupBy.path, item)
          if (value in res) res[value].items.push(item)
          else res[value] = { label: this.$path(this.groupBy.label, item), items: [item], value }
          return res
        }, {}),
      )
    },
    itemsComp() {
      return this.items instanceof Array ? this.items : this.fetchedItems
    },
    hasSearch() {
      return this.search && typeof this.items === 'function'
    },
  },
  methods: {
    performQuery: Debounce(function () {
      this.internalfetch(this.filter)
    }, 200),
    internalfetch(filter) {
      if (this.items instanceof Array) return Promise.resolve(this.items)
      return (this.promises.fetch = this.items(filter).then(data => (this.fetchedItems = data)))
    },
    isSelected(path, value) {
      if (!path) return this.value.length === this.itemsComp.length
      let items = this.getGroupedItems(path, value)
      return items.every(i => this.value.includes(i.value))
    },
    getGroupedItems(path, value) {
      return this.itemsComp.filter(i => this.$path(path, i) === value)
    },
    selectMany(state, path, value) {
      if (!path) return this.$emit('input', state ? this.itemsComp.map(i => i.value) : [])
      else {
        let items = this.getGroupedItems(path, value).map(i => i.value)
        if (state) return this.$emit('input', [...this.value, ...items])
        else
          return this.$emit(
            'input',
            this.value.filter(v => !items.includes(v)),
          )
      }
    },
  },
  components: {
    InputComponent,
    CheckboxComponent,
    LoaderComponent,
  },
}
</script>

<style lang="scss">
.select-checklist {
  &__search {
    margin-bottom: 1rem;
  }

  &__items {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
}
</style>
